import { useState, useEffect } from 'react';
import { Target, Info } from 'lucide-react';

interface ProfitLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (limit: number) => void;
  currentLimit: number; // 0 = disabled
  isLoading?: boolean;
}

const PRESET_AMOUNTS = [200, 400, 600, 800, 1000];

export function ProfitLimitDialog({
  isOpen,
  onClose,
  onConfirm,
  currentLimit,
  isLoading = false,
}: ProfitLimitDialogProps) {
  const [amount, setAmount] = useState(currentLimit > 0 ? currentLimit : 400);
  const [enabled, setEnabled] = useState(currentLimit > 0);
  const [customInput, setCustomInput] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const isEnabled = currentLimit > 0;
      setEnabled(isEnabled);
      setAmount(isEnabled ? currentLimit : 400);
      setIsCustom(!PRESET_AMOUNTS.includes(currentLimit) && currentLimit > 0);
      setCustomInput(isEnabled && !PRESET_AMOUNTS.includes(currentLimit) ? currentLimit.toString() : '');
    }
  }, [isOpen, currentLimit]);

  if (!isOpen) return null;

  const effectiveAmount = isCustom ? (parseFloat(customInput) || 0) : amount;
  const isValid = !enabled || effectiveAmount > 0;

  const handleConfirm = () => {
    if (!enabled) {
      onConfirm(0);
    } else {
      onConfirm(effectiveAmount);
    }
  };

  const handlePresetClick = (preset: number) => {
    setAmount(preset);
    setIsCustom(false);
    setCustomInput('');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-5">
        {/* Title */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Daily Profit Target
          </h2>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Profit Target
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {enabled ? `Stop buying at $${effectiveAmount.toLocaleString()}` : 'Disabled'}
            </div>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              enabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Amount Selection (only when enabled) */}
        {enabled && (
          <>
            {/* Preset Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Daily profit threshold
              </label>
              <div className="grid grid-cols-5 gap-2">
                {PRESET_AMOUNTS.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => handlePresetClick(preset)}
                    className={`py-2 px-1 text-sm font-medium rounded-lg border transition-colors ${
                      !isCustom && amount === preset
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-green-400'
                    }`}
                  >
                    ${preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Input */}
            <div>
              <button
                onClick={() => {
                  setIsCustom(true);
                  setCustomInput(effectiveAmount > 0 ? effectiveAmount.toString() : '');
                }}
                className={`text-sm font-medium ${
                  isCustom ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400 hover:text-green-600'
                }`}
              >
                {isCustom ? 'Custom amount:' : 'Enter custom amount...'}
              </button>
              {isCustom && (
                <div className="mt-2 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    min="1"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    placeholder="Enter amount"
                    autoFocus
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Info */}
        <div className="flex items-start space-x-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <Info className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            When your total daily realized profit across all algorithms reaches this target, all new buys are paused. Open positions still close normally via sell triggers. Resets at midnight PT.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex space-x-3 pt-1">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || !isValid}
            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

